import { contractApi } from '../contractApi';
import { DrgView, FeeSummaryView } from '../types/contractTypes';
import { DrgGetRequest } from '../requests/drgGetRequest';
import { DrgsByFeeScheduleIdRequest } from '../requests/drgsByFeeScheduleIdRequest';
import { DrgUpsertRequest } from '../requests/drgUpsertRequest';

const drgEndpoints = contractApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get DRG by ID
    getDrg: builder.query<DrgView, DrgGetRequest>({
      query: ({ id }) => ({
        url: 'DrgGetById',
        params: { id },
      }),
      providesTags: (result, error, { id }) => [{ type: 'Drg', id }, 'Drg'],
    }),

    // Get all DRGs by fee schedule ID
    getDrgsByFeeScheduleId: builder.query<DrgView[], DrgsByFeeScheduleIdRequest>({
      query: ({ feeScheduleId }) => ({
        url: 'Drgs',
        method: 'GET',
        params: { feeScheduleId },
      }),
      providesTags: (result, error, { feeScheduleId }) => [
        { type: 'DrgList', id: feeScheduleId },
        'Drg',
      ],
    }),

    // Upsert DRG (create or update)
    upsertDrg: builder.mutation<DrgView, DrgUpsertRequest>({
      query: (body) => ({
        url: 'Drg',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, body) => [
        ...(body.drg.id ? [{ type: 'Drg' as const, id: body.drg.id }] : []),
        { type: 'DrgList', id: body.drg.feeScheduleId },
        'Drg',
        'DrgList',
      ],
    }),

    // Delete DRG by ID
    deleteDrg: builder.mutation<void, { id: string; feeScheduleId: string }>({
      query: ({ id }) => ({
        url: 'DrgDelete',
        method: 'DELETE',
        params: { id },
      }),
      invalidatesTags: (result, error, { id, feeScheduleId }) => [
        { type: 'Drg', id },
        { type: 'DrgList', id: feeScheduleId },
        'Drg',
        'DrgList',
      ],
    }),

    // Upload DRGs via CSV file
    uploadDrgs: builder.mutation<FeeSummaryView, { feeScheduleId: string; file: File }>({
      query: ({ feeScheduleId, file }) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          url: `FeeSchedule/${feeScheduleId}/Drgs/Upload`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { feeScheduleId }) => [
        { type: 'DrgList', id: feeScheduleId },
        { type: 'FeeSchedule', id: feeScheduleId },
        'Drg',
        'DrgList',
        'FeeSchedule',
      ],
    }),

    // Download DRGs as CSV file
    downloadDrgs: builder.mutation<Blob, { feeScheduleId: string }>({
      query: ({ feeScheduleId }) => ({
        url: `FeeSchedule/${feeScheduleId}/Drgs/Download`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetDrgQuery,
  useGetDrgsByFeeScheduleIdQuery,
  useUpsertDrgMutation,
  useDeleteDrgMutation,
  useUploadDrgsMutation,
  useDownloadDrgsMutation,
} = drgEndpoints;
